import type { useRouter } from "@tanstack/react-router";

import type { DraftId } from "../draftId";
import { draftRightPanelKey, useRightPanelStore } from "../rightPanelStore";

/**
 * Show a draft, optionally with its browser panel already open.
 *
 * The panel is revealed *before* navigating, not after. The store applies
 * synchronously, so the draft route renders once with the panel already
 * present; revealing afterwards would paint the route without it and then open
 * it, which reads as a flicker on every "open browser" entry point.
 */
export async function openDraftRoute(input: {
  readonly router: ReturnType<typeof useRouter>;
  readonly draftId: DraftId;
  readonly openBrowserPreview?: boolean | undefined;
  readonly viewTransition?: boolean | undefined;
}): Promise<void> {
  if (input.openBrowserPreview) {
    useRightPanelStore.getState().revealBrowser(draftRightPanelKey(input.draftId));
  }
  const animate =
    input.viewTransition &&
    typeof document !== "undefined" &&
    typeof document.startViewTransition === "function";
  const navigate = () =>
    input.router.navigate({
      to: "/draft/$draftId",
      params: { draftId: input.draftId },
      ...(animate ? { viewTransition: false } : {}),
    });
  if (!animate) {
    await navigate();
    return;
  }

  const transition = document.startViewTransition(navigate);
  // A skipped animation rejects ready even when the draft opens successfully.
  // Keep navigation failures observable through updateCallbackDone.
  void transition.ready.catch(() => undefined);
  await transition.updateCallbackDone;
}
